import { Col, Flex, Input, Row, Select, Form, notification } from 'antd';
import { Heading } from '../Heading';
import { useTranslation } from 'react-i18next';
import { Card } from '../Card';
import ImageRequirementCard from './ImageRequirementCard';
import { THEME_VARIABLE } from '@/config';
import { EditOutline, CheckOutline } from '../Icons';
import { useFetchCompany, useUpdateCompany, useUploadCompanyLogo } from '@/hooksApi'; // Use your custom hooks
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

interface CompanyInfoProps {
  disabled?: boolean;
}

const CompanyInfo = ({ disabled = false }: CompanyInfoProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [form] = Form.useForm();

  // Fetch company details for pre-populating form fields
  const { data: company } = useFetchCompany();

  // Update company info mutation
  const { mutate: updateCompany, isLoading: isUpdating } = useUpdateCompany();

  // Upload company logo mutation
  const { mutate: uploadLogo, isLoading: isUploading } = useUploadCompanyLogo();

  // State to track if there are unsaved changes
  const [hasChanges, setHasChanges] = useState(false);

  // Pre-populate form with existing user data
  useEffect(() => {
    if (company?.object) {
      form.setFieldsValue({
        name: company?.object.name || '',
        industry: company?.object.industry || '',
      });
    }
  }, [company, form]);

  // Handle form value change and mark as having unsaved changes
  const handleFormChange = () => {
    setHasChanges(true); // Mark form as having unsaved changes
  };

  // Handle form submission for updating company info
  const handleFormSubmit = (values: any) => {
    const { name, industry } = values;
    updateCompany(
      { name, industry },
      {
        onSuccess: (updatedData) => {
          notification.success({
            message: t('success'),
            description: t('success-update-profile'),
          });

          // Refetch user data and set updated values in the form
          queryClient.invalidateQueries(['company']).then(() => {
            form.setFieldsValue({
              name: updatedData?.object.name || '',
              industry: updatedData?.object.industry || '',
            });
            setHasChanges(false);
          });
        },
        onError: () => {
          notification.error({
            message: t('error'),
            description: t('failed-update-profile'),
          });
          setHasChanges(false);
        },
      },
    );
  };

  // Handle logo upload
  const handleLogoUpload = (file: any) => {
    const formData = new FormData();
    formData.append('file', file.file);

    uploadLogo(formData, {
      onSuccess: () => {
        notification.success({
          message: t('success'),
          description: t('success-update-avatar'),
        });
        queryClient.invalidateQueries(['company']); // Refetch company data after logo upload
      },
      onError: () => {
        notification.error({
          message: t('error'),
          description: t('failed-update-avatar'),
        });
      },
    });
  };

  return (
    <Flex vertical className='mt-[30px]'>
      <Heading
        title={t('company_info')}
        fontSize='text-lg'
        fontWeight='font-medium'
        extraClassName='uppercase'
      />
      <Card extraClassName='mt-3'>
        <ImageRequirementCard
          handleAvatarUpload={handleLogoUpload} // Use for logo upload
          isAvatarUploading={isUploading}
          avatarUrl={company?.object?.avatar?.abs_url} // Provide the current avatar URL if available
          disabled={disabled}
        />

        <Form form={form} onFinish={handleFormSubmit} onValuesChange={handleFormChange}>
          <Row gutter={[24, 16]} className='mt-6'>
            <Col lg={21} md={24} xs={24}>
              <Form.Item
                name='name'
                rules={[{ required: true, message: t('Company name is required') }]}
              >
                <Input
                  placeholder={t('company_name')}
                  bordered={false}
                  disabled={disabled}
                  className='border border-solid placeholder:capitalize placeholder:font-normal placeholder:text-base placeholder:leading-[20.48px] placeholder:text-GrayShade4 border-customGrayShade5 rounded-lg h-[56px]'
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[24, 16]} className='mt-4'>
            <Col lg={21} md={24} xs={24}>
              <Form.Item
                name='industry'
                rules={[{ required: true, message: t('Industry is required') }]}
              >
                <Select
                  bordered={false}
                  placeholder={t('industry')}
                  disabled={disabled}
                  className='w-full border border-solid custom-select-placeholder border-customGrayShade5 rounded-lg h-[56px]'
                >
                  <Select.Option value='beauty'>{t('beauty')}</Select.Option>
                  <Select.Option value='online_shop'>{t('online_shop')}</Select.Option>
                  <Select.Option value='technology'>{t('technology')}</Select.Option>
                  <Select.Option value='finance'>{t('finance')}</Select.Option>
                  <Select.Option value='healthcare'>{t('healthcare')}</Select.Option>
                  <Select.Option value='education'>{t('education')}</Select.Option>
                  <Select.Option value='real_estate'>{t('real_estate')}</Select.Option>
                  <Select.Option value='retail'>{t('retail')}</Select.Option>
                  <Select.Option value='manufacturing'>{t('manufacturing')}</Select.Option>
                  <Select.Option value='hospitality'>{t('hospitality')}</Select.Option>
                  <Select.Option value='transportation'>{t('transportation')}</Select.Option>
                  <Select.Option value='agriculture'>{t('agriculture')}</Select.Option>
                  {/* Add more options as needed */}
                </Select>
              </Form.Item>
            </Col>
            <Col lg={3} md={24} xs={24}>
              <button
                type='submit'
                disabled={isUpdating || disabled}
                className={`flex justify-center items-center w-[56px] min-h-[56px] rounded-[10px] border border-solid cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                  hasChanges
                    ? 'bg-[rgb(237,224,74)] border-[rgb(237,224,74)] hover:bg-[rgb(230,217,60)]'
                    : 'bg-white border-[rgb(57,57,60)] hover:bg-[rgb(245,245,243)]'
                }`}
              >
                {isUpdating ? (
                  <span className='h-3.5 w-3.5 rounded-full border-2 border-[rgb(57,57,60)] border-t-transparent animate-spin' />
                ) : hasChanges ? (
                  <CheckOutline width={24} height={24} color={THEME_VARIABLE.customTunaPrimary} />
                ) : (
                  <EditOutline width={24} height={24} color={THEME_VARIABLE.customTunaPrimary} />
                )}
              </button>
            </Col>
          </Row>
        </Form>
      </Card>
    </Flex>
  );
};

export default CompanyInfo;
